import { tuneInEventValidators, } from '@tunein/web-schema-validator';
import { isBrowser, merge, noop } from 'es-toolkit';
import { AB_TEST_IDS } from '../../../constants';
import { v4 as uuidv4 } from 'uuid';
import { QueueBatchProcessor } from './QueueBatchProcessor';
import { ScreenViewManager, } from './ScreenViewManager';
import { UnifiedEventsSdk } from './UnifiedEventsSdk.generated';
import { BATCH_REPORTS_INTERVAL_SECS, MAX_BATCH, REPORTING_EVENT_TYPE, sendImmediatelyEventsSet, } from './constants';
export class UnifiedEventsService extends UnifiedEventsSdk {
    #requestClient;
    #storeClient;
    #queueBatchProcessor;
    #screenViewManager;
    #sessionId;
    constructor(requestClient, storeClient) {
        super();
        this.#requestClient = requestClient;
        this.#storeClient = storeClient;
        this.#sessionId = uuidv4();
        const { reportingIntervalDelaySecs, reportingMaxBatch } = this.#storeClient.options.unifiedEvents;
        this.#queueBatchProcessor = new QueueBatchProcessor({
            callback: this.#sendBatch,
            intervalDelaySecs: reportingIntervalDelaySecs || BATCH_REPORTS_INTERVAL_SECS,
            maxBatch: reportingMaxBatch || MAX_BATCH,
        });
        this.#screenViewManager = new ScreenViewManager();
        if (isBrowser()) {
            this.#queueBatchProcessor.init();
            this.#screenViewManager.on('change', this.#onScreenViewChange);
        }
    }
    sendFormattedUnifiedEvent = (event) => {
        return this.#sendBatch([event]);
    };
    setScreen(screenConfig) {
        this.#screenViewManager.setScreen(screenConfig);
    }
    clearScreen() {
        this.#screenViewManager.clearScreen();
    }
    #onScreenViewChange = (event) => {
        const { name, guideId, isVisible } = event;
        const { sendScreenViewReportsAsBeacon } = this.#storeClient.options;
        this.#storeClient.updateReportingContext({
            appState: {
                inBackground: !isVisible,
            },
        });
        return this.reportSandbox({
            category: name,
            action: isVisible ? 'screen_start' : 'screen_stop',
            props: { guideId },
        }, sendScreenViewReportsAsBeacon
            ? { sendAsBeacon: true }
            : { sendImmediately: true });
    };
    async _processEventReport(validatorKey, eventPayload, options = {}) {
        const validator = await tuneInEventValidators[validatorKey]();
        if (validator.name === 'JSON_SCHEMA_FAILED_TO_LOAD') {
            this.#onValidationError(validator.name, new Error(validator.name));
            return;
        }
        const eventData = {
            type: REPORTING_EVENT_TYPE,
            messageId: uuidv4(),
            eventTs: new Date().toJSON(),
            context: this.#buildContext(),
            event: validator.name,
            ...eventPayload,
        };
        let errors;
        try {
            errors = validator.validate(eventData);
        }
        catch (error) {
            this.#onValidationError(validator.name, error);
            return;
        }
        if (errors) {
            errors.forEach((error) => this.#onValidationError(validator.name, error));
            if (!errors.every(({ keyword }) => keyword === 'required')) {
                return;
            }
        }
        if (options.sendImmediately ||
            sendImmediatelyEventsSet.has(validator.name) ||
            !isBrowser()) {
            this.#sendBatch([eventData]).catch(noop);
            return;
        }
        if (options.sendAsBeacon) {
            const { local } = this.#storeClient.options.domains;
            const eventBlob = new Blob([JSON.stringify(eventData)], {
                type: 'application/json',
            });
            navigator.sendBeacon(`${local}/unified-event`, eventBlob);
            return;
        }
        this.#queueBatchProcessor.addToQueue(eventData);
    }
    #sendBatch = (batch) => {
        const { serial, domains } = this.#storeClient.options;
        const eventsApi = `${domains.event}/events/client-reports`;
        return this.#requestClient.fetch({
            url: eventsApi,
            post: {
                device_id: serial,
                sent_at: new Date().toJSON(),
                data: batch,
            },
            parseAs: 'text',
            skipUrlTransform: true,
            skipAllResponseTransforms: true,
        });
    };
    #buildContext = () => {
        const { partnerId, serial, version, ipAddress } = this.#storeClient.options;
        const hasAuthTokens = this.#requestClient.hasAuthTokens;
        const _isBrowser = isBrowser();
        const baseContext = {
            partnerId,
            deviceId: serial,
            sessionId: this.#sessionId,
            locale: _isBrowser ? window.navigator?.language || '' : '',
            userAgent: _isBrowser ? window.navigator?.userAgent || '' : '',
            timezone: typeof Intl !== 'undefined'
                ? new Intl.DateTimeFormat()?.resolvedOptions()?.timeZone || ''
                : '',
            ip: ipAddress || '',
            app: {
                version: version?.toString() || '',
            },
            appState: {
                abTestIds: this.#storeClient.getConfigSetting(AB_TEST_IDS, ''),
                inBackground: false,
            },
            page: {
                path: _isBrowser ? window.location?.pathname || '' : '',
            },
            screen: {
                height: _isBrowser ? window.screen?.height || 0 : 0,
                width: _isBrowser ? window.screen?.width || 0 : 0,
            },
            vehicle: {
                make: '',
                model: '',
                year: 0,
                isDistractionOptimizationRequired: false,
                drivingState: 'DRIVING_STATE_UNSPECIFIED',
            },
            userInfo: {
                userId: this.#storeClient.userData?.userName || '',
                isRegistered: hasAuthTokens,
                isLoggedIn: hasAuthTokens,
            },
            listeningInfo: {
                listenId: '',
                guideId: '',
                parentGuideId: '',
            },
            privacy: {
                gdpr: {
                    isApplicable: false,
                    status: 'CONSENT_STATUS_UNSPECIFIED',
                },
                globalOptIn: {
                    isApplicable: false,
                    status: 'CONSENT_STATUS_UNSPECIFIED',
                },
                globalOptOut: {
                    isApplicable: false,
                    status: 'CONSENT_STATUS_UNSPECIFIED',
                },
                usPrivacy: {
                    isApplicable: false,
                    status: 'CONSENT_STATUS_UNSPECIFIED',
                },
                systemAdTrackingStatus: 'SYSTEM_AD_TRACKING_STATUS_UNSPECIFIED',
                jurisdictionDetails: {
                    countryCode: this.#storeClient.locationData?.country_code || '',
                    stateCode: this.#storeClient.locationData?.state || '',
                },
                lastUpdatedAt: new Date().toISOString(),
            },
        };
        return merge(baseContext, this.#storeClient.reportingContext);
    };
    #onValidationError = (eventName, error) => {
        const { debug } = this.#storeClient.options;
        if (debug) {
            const errorDetails = error instanceof Error
                ? { name: error.name, message: error.message, stack: error.stack }
                : error;
            console.error('Schema validator error', eventName, JSON.stringify(errorDetails, null, 2));
        }
        this.#requestClient.emit('error', {
            message: 'Schema Validation Error',
            context: {
                eventName,
                message: error.message,
                error: error instanceof Error
                    ? { name: error.name, message: error.message }
                    : error,
                pagePath: isBrowser() ? window.location?.pathname || '' : '',
            },
        });
    };
}
